import { create } from 'zustand';

/**
 * LockTypes defines the different types of locks available in the system
 * Used to prevent race conditions and unwanted updates
 */
export type LockType = 'address' | 'map' | 'search' | 'position';

// Maximum time a lock can be held before auto-releasing (in milliseconds)
const MAX_LOCK_DURATION = 10000; // 10 seconds

/**
 * LockManager interface defining the state and actions available
 */
interface LockManagerState {
  // Lock states
  locks: Record<LockType, boolean>;
  
  // Timestamps to track when locks were created
  lockTimestamps: Record<LockType, number>;
  
  // Timeout IDs for auto-unlock
  lockTimeouts: Record<LockType, number | null>;
  
  // Actions
  lock: (type: LockType, reason?: string) => void;
  unlock: (type: LockType, reason?: string) => void;
  isLocked: (type: LockType) => boolean;
  
  // Multiple locks
  lockMultiple: (types: LockType[], reason?: string) => void;
  unlockMultiple: (types: LockType[], reason?: string) => void;
  
  // Reset all locks
  resetAllLocks: () => void;
  
  // Check for stale locks
  checkAndReleaseStaleAllLocks: () => void;
}

/**
 * Lock manager store using Zustand
 * Provides a centralized way to manage various types of locks
 */
export const useLockManager = create<LockManagerState>((set, get) => ({
  // Initial lock states - all unlocked
  locks: {
    address: false,
    map: false,
    search: false,
    position: false
  },
  
  // Initial timestamps
  lockTimestamps: {
    address: 0,
    map: 0,
    search: 0,
    position: 0
  },
  
  // Timeout IDs for auto-unlock
  lockTimeouts: {
    address: null,
    map: null,
    search: null,
    position: null
  },
  
  /**
   * Lock a specific type
   */
  lock(type: LockType, reason = 'not specified') {
    console.log(`[LockManager] Locking ${type} - Reason: ${reason}`);
    
    // Clear any existing timeout for this lock
    const currentTimeout = get().lockTimeouts[type];
    if (currentTimeout) {
      clearTimeout(currentTimeout);
    }
    
    // Set a new timeout to auto-unlock after MAX_LOCK_DURATION
    const timeoutId = setTimeout(() => {
      const state = get();
      // Only auto-unlock if the lock is still from this lock operation
      // (check timestamp to make sure it's the same lock)
      if (state.locks[type] && Date.now() - state.lockTimestamps[type] >= MAX_LOCK_DURATION) {
        console.warn(`[LockManager] Auto-unlocking ${type} - Lock timeout exceeded (${MAX_LOCK_DURATION}ms)`);
        get().unlock(type, 'auto-unlock due to timeout');
      }
    }, MAX_LOCK_DURATION);
    
    set(state => ({
      locks: {
        ...state.locks,
        [type]: true
      },
      lockTimestamps: {
        ...state.lockTimestamps,
        [type]: Date.now()
      },
      lockTimeouts: {
        ...state.lockTimeouts,
        [type]: timeoutId
      }
    }));
  },
  
  /**
   * Unlock a specific type
   */
  unlock(type: LockType, reason = 'not specified') {
    console.log(`[LockManager] Unlocking ${type} - Reason: ${reason}`);
    
    // Clear the timeout for this lock
    const currentTimeout = get().lockTimeouts[type];
    if (currentTimeout) {
      clearTimeout(currentTimeout);
    }
    
    set(state => ({
      locks: {
        ...state.locks,
        [type]: false
      },
      lockTimeouts: {
        ...state.lockTimeouts,
        [type]: null
      }
    }));
  },
  
  /**
   * Check if a specific type is locked
   */
  isLocked(type: LockType): boolean {
    return get().locks[type];
  },
  
  /**
   * Lock multiple types at once
   */
  lockMultiple(types: LockType[], reason = 'not specified') {
    console.log(`[LockManager] Locking multiple: ${types.join(', ')} - Reason: ${reason}`);
    
    const now = Date.now();
    set(state => {
      const newLocks = { ...state.locks };
      const newTimestamps = { ...state.lockTimestamps };
      const newTimeouts = { ...state.lockTimeouts };
      
      types.forEach(type => {
        // Clear any existing timeout
        if (newTimeouts[type]) {
          clearTimeout(newTimeouts[type] as number);
        }
        
        // Set the lock
        newLocks[type] = true;
        newTimestamps[type] = now;
        
        // Create a new timeout
        newTimeouts[type] = setTimeout(() => {
          const currentState = get();
          if (currentState.locks[type] && Date.now() - currentState.lockTimestamps[type] >= MAX_LOCK_DURATION) {
            console.warn(`[LockManager] Auto-unlocking ${type} - Lock timeout exceeded (${MAX_LOCK_DURATION}ms)`);
            get().unlock(type, 'auto-unlock due to timeout');
          }
        }, MAX_LOCK_DURATION) as unknown as number;
      });
      
      return {
        locks: newLocks,
        lockTimestamps: newTimestamps,
        lockTimeouts: newTimeouts
      };
    });
  },
  
  /**
   * Unlock multiple types at once
   */
  unlockMultiple(types: LockType[], reason = 'not specified') {
    console.log(`[LockManager] Unlocking multiple: ${types.join(', ')} - Reason: ${reason}`);
    
    set(state => {
      const newLocks = { ...state.locks };
      const newTimeouts = { ...state.lockTimeouts };
      
      types.forEach(type => {
        // Clear timeout
        if (newTimeouts[type]) {
          clearTimeout(newTimeouts[type] as number);
          newTimeouts[type] = null;
        }
        
        // Unlock
        newLocks[type] = false;
      });
      
      return {
        locks: newLocks,
        lockTimeouts: newTimeouts
      };
    });
  },
  
  /**
   * Reset all locks to unlocked state
   */
  resetAllLocks() {
    console.log('[LockManager] Resetting all locks');
    
    // Clear all timeouts
    Object.entries(get().lockTimeouts).forEach(([type, timeout]) => {
      if (timeout) {
        clearTimeout(timeout);
      }
    });
    
    set({
      locks: {
        address: false,
        map: false,
        search: false,
        position: false
      },
      lockTimeouts: {
        address: null,
        map: null,
        search: null,
        position: null
      }
    });
  },
  
  /**
   * Check for and release any stale locks
   */
  checkAndReleaseStaleAllLocks() {
    const state = get();
    const now = Date.now();
    const staleTypes: LockType[] = [];
    
    // Find any stale locks
    (Object.entries(state.locks) as [LockType, boolean][]).forEach(([type, isLocked]) => {
      if (isLocked && now - state.lockTimestamps[type] >= MAX_LOCK_DURATION) {
        staleTypes.push(type);
      }
    });
    
    // Unlock any stale locks
    if (staleTypes.length > 0) {
      console.warn(`[LockManager] Found stale locks: ${staleTypes.join(', ')}`);
      get().unlockMultiple(staleTypes, 'cleanup of stale locks');
    }
  }
}));

/**
 * Helper function to run a function with locked state
 * Ensures the lock is released even if an error occurs
 */
export const withLock = async <T>(
  lockType: LockType | LockType[],
  fn: () => Promise<T>,
  reason = 'operation'
): Promise<T> => {
  const types = Array.isArray(lockType) ? lockType : [lockType];
  
  try {
    // Lock the specified types
    useLockManager.getState().lockMultiple(types, reason);
    
    // Run the function
    return await fn();
  } finally {
    // Ensure locks are released even if an error occurs
    useLockManager.getState().unlockMultiple(types, `${reason} completed`);
  }
};

/**
 * Helper function to create a debounced function that respects locks
 */
export const createLockedDebounce = <T extends (...args: any[]) => any>(
  lockType: LockType,
  fn: T,
  wait: number,
  reason = 'debounced operation'
): (...args: Parameters<T>) => void => {
  let timeout: number | null = null;
  
  return (...args: Parameters<T>) => {
    // Check if already locked
    if (useLockManager.getState().isLocked(lockType)) {
      console.log(`[LockedDebounce] Skipping debounced function - ${lockType} is locked`);
      return;
    }
    
    // Clear existing timeout
    if (timeout) clearTimeout(timeout);
    
    // Lock the type
    useLockManager.getState().lock(lockType, `${reason} - debounce started`);
    
    // Set new timeout
    timeout = setTimeout(async () => {
      try {
        await fn(...args);
      } catch (error) {
        console.error(`[LockedDebounce] Error in debounced function:`, error);
      } finally {
        // Always unlock when complete
        useLockManager.getState().unlock(lockType, `${reason} - debounce executed`);
      }
    }, wait);
  };
}; 